import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileClock, Trash2, ArrowRight } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ConfirmDelete } from "./ConfirmDelete";
import { dateTimeBR } from "../lib/format";
import { SHORTCUTS } from "../lib/requestForms";
import { Button } from "./ui/button";

const STEP = { 1: "Dados básicos", 2: "Estrutura", 3: "Benefícios", 4: "Revisão" };
const until = (iso) => new Date(iso).toLocaleDateString("pt-BR");
export const FORM_LABEL = { ...Object.fromEntries(SHORTCUTS.map((s) => [s.type, s.label])), duvida: "Dúvida", documento: "Documento", alteracao_cadastral: "Alteração cadastral", comissao: "Comissão", variavel: "Outras variáveis" };

export const DRAFTS_EVT = "dp:drafts-changed";
const deleted = new Set();
// Ids deleted in this tab: autosave never sends them again.
export const isDeletedDraft = (id) => deleted.has(id);
export async function deleteDraft(id) {
  deleted.add(id);
  try {
    const r = await api.delete(`/admission-drafts/${id}`);
    window.dispatchEvent(new CustomEvent(DRAFTS_EVT, { detail: { deleted: id } }));
    return r.data;
  } catch (e) { deleted.delete(id); throw e; }
}
const noun = (f) => (FORM_LABEL[f] || "solicitação").replace(/^Nova /, "").toLowerCase();

export function DraftDeleteDialog({ draft, onClose, onDeleted }) {
  const go = async () => {
    try {
      await deleteDraft(draft.id);
      toast.success("Rascunho excluído definitivamente.");
      onDeleted?.(draft.id);
      return true;
    } catch (e) { toast.error(`Não foi possível excluir o rascunho. ${errMsg(e)} Tente novamente.`); return false; }
  };
  return <ConfirmDelete open={!!draft} onClose={onClose} onConfirm={go} testid="draft-delete" title="Excluir rascunho?"
    message={`Tem certeza de que deseja excluir este rascunho de ${noun(draft?.form || "admissao")}${draft?.name ? ` (${draft.name})` : ""}? Esta ação é definitiva e não poderá ser desfeita.`} />;
}

// Server-side drafts of the current user for one form (30 days).
export function DraftPicker({ form, drafts, onResume, onDelete, onNew }) {
  if (!drafts?.length) return null;
  const adm = form === "admissao";
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="draft-picker">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900"><FileClock className="h-4 w-4" />Continuar rascunho</div>
      <ul className="space-y-1.5">{drafts.map((d) => (
        <li key={d.id} className="flex items-center gap-2 rounded-lg bg-white border px-3 py-2 text-sm" data-testid={`draft-item-${d.id}`}>
          <span className="flex-1 min-w-0"><span className="block truncate font-medium">{d.name || "Sem nome"}</span>
            <span className="block text-[11px] text-slate-500">{adm ? `Etapa ${d.step} · ${STEP[d.step]} · ` : ""}alterado {dateTimeBR(d.updated_at)} · apaga em {until(d.expires_at)}</span></span>
          <Button type="button" size="sm" onClick={() => onResume(d.id)} data-testid={`draft-resume-${d.id}`}>Continuar</Button>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" title="Excluir rascunho" onClick={() => onDelete(d.id)} data-testid={`draft-delete-${d.id}`}><Trash2 className="h-4 w-4 text-slate-500" /></Button>
        </li>))}</ul>
      <Button type="button" size="sm" variant="outline" className="bg-white" onClick={onNew} data-testid="draft-new">{adm ? "Começar nova admissão" : "Começar do zero"}</Button>
    </div>);
}

// Home notice: always reflects the drafts that really exist on the server.
export function DraftsBanner({ refresh, onOpen }) {
  const [list, setList] = useState([]);
  const [manage, setManage] = useState(false);
  const [rm, setRm] = useState(null);
  const seq = useRef(0);
  const load = () => { const n = ++seq.current; api.get("/admission-drafts").then((r) => n === seq.current && setList(r.data)).catch(() => n === seq.current && setList([])); };
  useEffect(load, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { window.addEventListener(DRAFTS_EVT, load); return () => window.removeEventListener(DRAFTS_EVT, load); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dialog = <DraftDeleteDialog draft={rm} onClose={() => setRm(null)} onDeleted={(id) => setList((l) => l.filter((d) => d.id !== id))} />;
  if (!list.length) return dialog;
  const by = list.reduce((m, d) => ({ ...m, [d.form]: (m[d.form] || 0) + 1 }), {});
  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="drafts-banner">
      <div className="flex flex-wrap items-center gap-2">
        <FileClock className="h-4 w-4" /><span className="mr-1" data-testid="drafts-banner-text">Você tem {list.length} {list.length === 1 ? "rascunho" : "rascunhos"}:</span>
        {Object.entries(by).map(([f, n]) => (
          <Button key={f} size="sm" variant="outline" className="h-7 bg-white" onClick={() => onOpen(f)} data-testid={`drafts-banner-open-${f}`}>
            {FORM_LABEL[f] || "Solicitação"}{n > 1 ? ` (${n})` : ""} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>))}
        <Button size="sm" variant="ghost" className="ml-auto h-7 text-amber-900 hover:bg-amber-100" onClick={() => setManage(!manage)} data-testid="drafts-banner-manage">
          <Trash2 className="h-3.5 w-3.5 mr-1" />{manage ? "Fechar" : "Excluir rascunhos"}</Button>
      </div>
      {manage && <ul className="mt-2 space-y-1.5" data-testid="drafts-banner-list">{list.map((d) => (
        <li key={d.id} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5" data-testid={`drafts-banner-item-${d.id}`}>
          <span className="flex-1 min-w-0 truncate"><b className="font-medium">{FORM_LABEL[d.form] || "Solicitação"}</b>{d.name ? ` · ${d.name}` : ""}
            <span className="ml-1 text-[11px] text-slate-500">alterado {dateTimeBR(d.updated_at)}</span></span>
          <Button size="icon" variant="ghost" className="h-7 w-7" title="Excluir rascunho" onClick={() => setRm(d)} data-testid={`drafts-banner-delete-${d.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
        </li>))}</ul>}
      {dialog}
    </div>);
}
